/**
 * Excel-exact formula implementations for ESAL calculations
 * Based on exact formulas from temp_esal_material/TRAFFIC LOAD.txt
 *
 * IMPORTANT: This class now supports both:
 * 1. Dynamic calculations using Excel formulas (recommended for accuracy)
 * 2. Legacy hardcoded values for backward compatibility
 *
 * Dynamic calculations properly handle:
 * - USER LOAD vs DESIGN LOAD scenarios
 * - Dynamic steering axle calculations
 * - Proper axle load distribution based on truck configuration
 * - Real-time EALF calculations using Excel formulas
 */

import { ESAL_CONSTANTS, SummaryCalculationData, TruckData, LoadingScenarioConfig, DEFAULT_LOADING_CONFIG } from './types/ESALTypes';
import { AircraftData, AircraftConfiguration, DEFAULT_AIRCRAFT_CONFIGURATIONS } from './types/ESALTypesAircraft';
import { StructuralNumberService } from '../../../services/StructuralNumberService';
import { IProjectData } from '../../../api/IApiResponse';
import { TruckConfigurationService } from '../../../services/TruckConfigurationService';

/**
 * Excel-exact ESAL calculation class
 * Based on AASHTO methodology from TRAFFIC LOAD.txt
 */
export class ESALFormulas {
    // AASHTO pavement design constants - hardcoded to match Excel reference sheet
    private static readonly AASHTO_CONSTANTS = {
        /** Structural Number (Excel B4) - Fixed at 5.0 to match Excel calculations exactly */
        STRUCTURAL_NUMBER: 5.0,
        /** Present Serviceability (Excel B5) - Terminal serviceability level */
        PRESENT_SERVICEABILITY: 2.5,
        /** Initial Serviceability - AASHTO standard value */
        INITIAL_SERVICEABILITY: 4.2,
        /** Serviceability Loss Factor - AASHTO standard value */
        SERVICEABILITY_LOSS_FACTOR: 2.7,
    };

    // Excel constants from TRAFFIC LOAD.txt (calculated from AASHTO constants above)
    private static readonly EXCEL_CONSTANTS = {
        SN: ESAL_CONSTANTS.STRUCTURAL_NUMBER, // B4 = Structural Number (from AASHTO_CONSTANTS)
        PT: ESAL_CONSTANTS.PRESENT_SERVICEABILITY, // B5 = Present Serviceability (from AASHTO_CONSTANTS)
        GT: -0.20091484278071334, // T6 = LOG(((4.2-2.5)/2.7),10) = LOG(((INITIAL_SERVICEABILITY-PRESENT_SERVICEABILITY)/SERVICEABILITY_LOSS_FACTOR),10)
        BETA_18: 0.5000585397376548, // T7 = 0.4+(0.081*(19^3.23))/((5+1)^5.19) = calculated from AASHTO constants
        T8: ESAL_CONSTANTS.AXLE_CONSTANTS.STEERING, // Constant for steering axle
        V8: ESAL_CONSTANTS.AXLE_CONSTANTS.SINGLE, // Constant for single axle
        X8: ESAL_CONSTANTS.AXLE_CONSTANTS.TANDEM, // Constant for tandem axle
        Z8: ESAL_CONSTANTS.AXLE_CONSTANTS.TRIDEM, // Constant for tridem axle
        KN_TO_KIPS: ESAL_CONSTANTS.KN_TO_KIPS, // Conversion factor (from ESAL_CONSTANTS)
    };

    /**
     * Round number to match Excel precision
     * Excel uses limited precision for intermediate calculations
     */
    private static roundToExcelPrecision(value: number, decimals: number): number {
        if (!isFinite(value)) return value;
        const factor = Math.pow(10, decimals);
        return Math.round(value * factor) / factor;
    }

    /**
     * Public access to AASHTO constants for external components
     * @returns AASHTO constants object with standard values
     */
    public static get CONSTANTS() {
        return {
            STRUCTURAL_NUMBER: ESALFormulas.AASHTO_CONSTANTS.STRUCTURAL_NUMBER,
            PRESENT_SERVICEABILITY: ESALFormulas.AASHTO_CONSTANTS.PRESENT_SERVICEABILITY,
            INITIAL_SERVICEABILITY: ESALFormulas.AASHTO_CONSTANTS.INITIAL_SERVICEABILITY,
            SERVICEABILITY_LOSS_FACTOR: ESALFormulas.AASHTO_CONSTANTS.SERVICEABILITY_LOSS_FACTOR,
        };
    }

    /**
     * Calculate GT (serviceability loss factor) dynamically from AASHTO constants
     * Formula: GT = LOG(((Pi - Pt) / ΔSI), 10)
     * Where: Pi = Initial Serviceability, Pt = Present Serviceability, ΔSI = Serviceability Loss Factor
     *
     * NOTE: Currently returns the same hardcoded value as EXCEL_CONSTANTS.GT (-0.20091484278071334)
     * This method is provided for future flexibility if PT needs to be made configurable
     *
     * @param presentServiceability - Terminal serviceability (default: 2.5)
     * @param initialServiceability - Initial serviceability (default: 4.2)
     * @param serviceabilityLossFactor - Serviceability loss factor (default: 2.7)
     * @returns GT value for EALF calculations
     */
    private static calculateGT(
        presentServiceability: number = ESALFormulas.AASHTO_CONSTANTS.PRESENT_SERVICEABILITY,
        initialServiceability: number = ESALFormulas.AASHTO_CONSTANTS.INITIAL_SERVICEABILITY,
        serviceabilityLossFactor: number = ESALFormulas.AASHTO_CONSTANTS.SERVICEABILITY_LOSS_FACTOR
    ): number {
        return Math.log10((initialServiceability - presentServiceability) / serviceabilityLossFactor);
    }

    /**
     * Calculate BETA_18 (base beta factor) dynamically from structural number
     * Formula: BETA_18 = 0.4 + (0.081 * (19^3.23)) / ((SN + 1)^5.19)
     *
     * NOTE: Currently returns the same hardcoded value as EXCEL_CONSTANTS.BETA_18 (0.5000585397376548) when SN=5.0
     * This method is provided for future flexibility if SN needs to be made configurable
     *
     * @param structuralNumber - Structural Number (default: 5.0)
     * @returns BETA_18 value for EALF calculations
     */
    private static calculateBeta18(structuralNumber: number = ESALFormulas.AASHTO_CONSTANTS.STRUCTURAL_NUMBER): number {
        return (
            ESAL_CONSTANTS.BETA_BASE +
            (ESAL_CONSTANTS.BETA_COEFFICIENT * Math.pow(ESAL_CONSTANTS.LOG_BASE, ESAL_CONSTANTS.AASHTO_EXPONENTS.LOAD_EXPONENT)) /
                Math.pow(structuralNumber + 1, ESAL_CONSTANTS.AASHTO_EXPONENTS.SN_EXPONENT)
        );
    }

    /**
     * Get truck-specific base steering load configuration from Excel
     * Based on Excel steering axle formulas for each truck type
     */
    private static getTruckSteeringConfig(truckId: number): { baseSteering: number; ratio: number } {
        const configs: Record<number, { baseSteering: number; ratio: number }> = {
            1: { baseSteering: 60, ratio: 60 }, // Camion1: F10 = IF(C13>0,60*C13/C12,60)
            2: { baseSteering: 60, ratio: 60 }, // Camion2: F14 = IF(C17>0,60*C17/C16,60)
            3: { baseSteering: 65, ratio: 65 }, // Camion3: F18 = IF(C21>0,65*C21/C20,65)
            4: { baseSteering: 65, ratio: 65 }, // Camion4: F22 = IF(C25>0,65*C25/C24,65)
            5: { baseSteering: 65, ratio: 65 }, // Camion5: F26 = IF(C29>0,65*C29/C28,65)
            6: { baseSteering: 65, ratio: 65 }, // Camion6: F30 = IF(C33>0,65*C33/C32,65)
            7: { baseSteering: 75, ratio: 75 }, // Camion7: F34 = IF(C37>0,75*C37/C36,75)
            8: { baseSteering: 75, ratio: 75 }, // Camion8: F38 = IF(C41>0,75*C41/C40,75)
        };
        return configs[truckId] || { baseSteering: 60, ratio: 60 }; // Default to Camion1 pattern
    }

    /**
     * Calculate dynamic steering axle load based on Excel formulas
     * Formula: IF(userLoad>0, baseRatio*userLoad/designLoad, baseSteering)
     */
    private static calculateSteeringAxleLoad(truck: TruckData): number {
        const config = this.getTruckSteeringConfig(truck.id);
        const userLoad = truck.customTotalLoad > 0 ? truck.customTotalLoad : 0;
        const designLoad = truck.designTotalLoad;

        if (userLoad > 0) {
            return (config.ratio * userLoad) / designLoad;
        }
        return config.baseSteering;
    }

    /**
     * Calculate Beta X for specific axle load in kips
     * Formula: =0.4+(0.081*((LX+T8)^3.23)/((($B$4+1)^5.19)*(T8^3.23)))
     */
    private static calculateBetaX(loadKips: number, axleConstant: number, structuralNumber: number): number {
        const betaX =
            ESAL_CONSTANTS.BETA_BASE +
            (ESAL_CONSTANTS.BETA_COEFFICIENT * Math.pow(loadKips + axleConstant, ESAL_CONSTANTS.AASHTO_EXPONENTS.LOAD_EXPONENT)) /
                (Math.pow(structuralNumber + 1, ESAL_CONSTANTS.AASHTO_EXPONENTS.SN_EXPONENT) * Math.pow(axleConstant, ESAL_CONSTANTS.AASHTO_EXPONENTS.LOAD_EXPONENT));
        // Round to 14 decimals to match Excel precision
        return this.roundToExcelPrecision(betaX, 14);
    }

    /**
     * Calculate EALF for specific axle load
     * Formula: =1/(((19/(LX+T8))^4.79)*((10^(GT/BetaX))/(10^(GT/Beta18))*(T8^4.33)))
     */
    private static calculateEALF(loadKips: number, axleConstant: number, structuralNumber: number): number {
        const { GT, BETA_18 } = this.EXCEL_CONSTANTS;

        if (loadKips <= 0) return 0;

        const betaX = this.calculateBetaX(loadKips, axleConstant, structuralNumber);

        const denominator =
            Math.pow(ESAL_CONSTANTS.LOG_BASE / (loadKips + axleConstant), ESAL_CONSTANTS.AASHTO_EXPONENTS.EALF_LOAD_EXPONENT) *
            (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) *
            Math.pow(axleConstant, ESAL_CONSTANTS.AASHTO_EXPONENTS.EALF_AXLE_EXPONENT);

        const ealf = 1 / denominator;
        // Round to 8 decimals to match Excel precision for EALF values
        return this.roundToExcelPrecision(ealf, 8);
    }

    /**
     * Excel-exact EALF values for each truck and loading scenario
     * These values are pre-calculated from TRAFFIC LOAD.txt to ensure exact match
     */
    private static readonly EXCEL_EALF_VALUES = {
        // Camion1 (2 axles, 180kN) - Q10 = 592.99
        1: {
            empty: 0.4802064776357546, // N10 - 20%
            halfFull: 2.8457569988133318, // N11 - 20%
            fullyLoaded: 7.425652264928494, // N12 - 50%
            overloaded: 15.519172402300626, // N13 - 10%
        },
        // Camion2 (3 axles, 250kN) - Q14 = 336.86
        2: {
            empty: 0.42534421624630137, // N14 - 20%
            halfFull: 1.1146178653616767, // N15 - 20%
            fullyLoaded: 4.0152301442661384, // N16 - 50%
            overloaded: 7.467572569914783, // N17 - 10%
        },
        // Camion3 (4 axles, 360kN) - Q18 = 942.90
        3: {
            empty: 0.6628276381221813, // N18 - 20%
            halfFull: 1.7940341094985908, // N19 - 20%
            fullyLoaded: 8.516852298300073, // N20 - 50%
            overloaded: 15.361810588123912, // N21 - 10%
        },
        // Camion4 (5 axles, 400kN) - Q22 = 586.38
        4: {
            empty: 0.6356838833245163, // N22 - 20%
            halfFull: 1.2045480271236961, // N23 - 20%
            fullyLoaded: 5.2355889531429725, // N24 - 50%
            overloaded: 9.233532280887387, // N25 - 10%
        },
        // Camion5 (5 axles, 400kN) - Q26 = 603.66
        5: {
            empty: 0.6357194093925685, // N26 - 20%
            halfFull: 1.2245972464523585, // N27 - 20%
            fullyLoaded: 5.397457651680712, // N28 - 50%
            overloaded: 9.536056023879864, // N29 - 10%
        },
        // Camion6 (6 axles, 400kN) - Q30 = 328.88
        6: {
            empty: 0.6352417210122487, // N30 - 20%
            halfFull: 0.8887154575167687, // N31 - 20%
            fullyLoaded: 2.8111769062656595, // N32 - 50%
            overloaded: 4.821707299066176, // N33 - 10%
        },
        // Camion7 (7 axles, 600kN) - Q34 = 0
        7: {
            empty: 0, // N34 - 20%
            halfFull: 0, // N35 - 20%
            fullyLoaded: 0, // N36 - 50%
            overloaded: 0, // N37 - 10%
        },
        // Camion8 (8 axles, 600kN) - Q38 = 0
        8: {
            empty: 0, // N38 - 20%
            halfFull: 0, // N39 - 20%
            fullyLoaded: 0, // N40 - 50%
            overloaded: 0, // N41 - 10%
        },
    };

    /**
     * Calculate EALF for a truck with specific load using dynamic calculation
     * This method calculates EALF based on exact Excel formulas for each axle type
     */
    private static calculateTruckEALFDynamic(truck: TruckData, totalLoadKips: number, structuralNumber: number): number {
        const axleLoads = this.calculateExcelAxleLoads(truck, totalLoadKips / this.EXCEL_CONSTANTS.KN_TO_KIPS);

        // Convert to kips for EALF calculations
        const steeringLoadKips = axleLoads.steeringAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;
        const singleLoadKips = axleLoads.singleAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;
        const tandemLoadKips = axleLoads.tandemAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;
        const tridemLoadKips = axleLoads.tridemAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;

        // Calculate EALF for each axle type using Excel formulas
        let totalEALF = 0;

        // Steering axle EALF: J = 1/(((19/(T+T8))^4.79)*((10^(GT/BetaX))/(10^(GT/Beta18))*(T8^4.33)))
        if (steeringLoadKips > 0) {
            totalEALF += this.calculateEALF(steeringLoadKips, this.EXCEL_CONSTANTS.T8, structuralNumber);
        }

        // Single axle EALF: K = 1/(((19/(V+V8))^4.79)*((10^(GT/BetaX))/(10^(GT/Beta18))*(V8^4.33)))
        if (singleLoadKips > 0) {
            totalEALF += this.calculateEALF(singleLoadKips, this.EXCEL_CONSTANTS.V8, structuralNumber);
        }

        // Tandem axle EALF: L = 1/(((19/(X+X8))^4.79)*((10^(GT/BetaX))/(10^(GT/Beta18))*(X8^4.33)))
        // For trucks with multiple tandem pairs, multiply by the count (e.g., L26*2 in Camion5)
        if (tandemLoadKips > 0) {
            const tandemEALF = this.calculateEALF(tandemLoadKips, this.EXCEL_CONSTANTS.X8, structuralNumber);
            totalEALF += tandemEALF * (axleLoads.tandemCount || 1);
        }

        // Tridem axle EALF: M = 1/(((19/(Z+Z8))^4.79)*((10^(GT/BetaX))/(10^(GT/Beta18))*(Z8^4.33)))
        if (tridemLoadKips > 0) {
            totalEALF += this.calculateEALF(tridemLoadKips, this.EXCEL_CONSTANTS.Z8, structuralNumber);
        }

        return totalEALF;
    }

    /**
     * Calculate exact truck ESAL using dynamic calculations instead of hardcoded values
     * This method uses Excel formulas to calculate everything dynamically
     */
    private static calculateTruckESALDynamicNew(truck: TruckData, vehiclesPerDay: number, structuralNumber: number, config: LoadingScenarioConfig = DEFAULT_LOADING_CONFIG): number {
        if (!truck.selected || vehiclesPerDay <= 0) return 0;

        // Get truck loading scenarios using dynamic Excel formulas
        const scenarios = this.getTruckLoadingScenarios(truck, config);
        let totalESAL = 0;

        // Calculate EALF for each loading scenario dynamically
        scenarios.forEach(scenario => {
            const loadKN = scenario.totalWeight;
            const loadKips = loadKN * this.EXCEL_CONSTANTS.KN_TO_KIPS;

            // Calculate EALF dynamically using exact Excel formulas
            const ealf = this.calculateTruckEALFDynamic(truck, loadKips, structuralNumber);

            // Calculate ESAL for this scenario: P = vehicles/day * EALF * percentage / 100
            const scenarioESAL = (vehiclesPerDay * ealf * scenario.percentage) / 100;
            totalESAL += scenarioESAL;
        });

        return totalESAL;
    }

    /**
     * Get axle count for a truck based on truck ID
     * Based on Excel truck definitions: Camion1=2, Camion2=3, Camion3=4, etc.
     * Data source: TruckConfigurationService (centralized JSON configuration)
     */
    private static getTruckAxleCount(truck: TruckData): number {
        return TruckConfigurationService.getAxleCount(truck.id);
    }

    /**
     * Get truck loading scenarios based on Excel dynamic formulas
     * Each truck has 4 scenarios with configurable percentages and multipliers
     * Using exact Excel formulas from TRAFFIC LOAD.txt
     */
    private static getTruckLoadingScenarios(
        truck: TruckData,
        config: LoadingScenarioConfig = DEFAULT_LOADING_CONFIG
    ): Array<{
        name: string;
        totalWeight: number;
        percentage: number;
    }> {
        const userLoad = truck.customTotalLoad > 0 ? truck.customTotalLoad : 0;
        const designLoad = truck.designTotalLoad;
        const steeringLoad = this.calculateSteeringAxleLoad(truck);

        // Excel formulas for loading scenarios with configurable multipliers:
        // EMPTY: E10 = IF(C13>0, F10+C13*emptyMultiplier, F10+C12*emptyMultiplier)
        // HALF FULL: E11 = IF(C13>0, F11+C13*halfFullMultiplier, F11+C12*halfFullMultiplier)
        // FULLY LOADED: E12 = IF(C13>0, C13, C12)
        // 15% OVERLOADED: E13 = IF(C13>0, C13*overloadMultiplier, C12*overloadMultiplier)

        const emptyWeight = userLoad > 0 ? steeringLoad + userLoad * config.emptyMultiplier : steeringLoad + designLoad * config.emptyMultiplier;

        const halfFullWeight = userLoad > 0 ? steeringLoad + userLoad * config.halfFullMultiplier : steeringLoad + designLoad * config.halfFullMultiplier;

        const fullyLoadedWeight = userLoad > 0 ? userLoad : designLoad;

        const overloadedWeight = userLoad > 0 ? userLoad * config.overloadMultiplier : designLoad * config.overloadMultiplier;

        return [
            {
                name: 'EMPTY',
                totalWeight: emptyWeight,
                percentage: config.emptyPercentage,
            },
            {
                name: 'HALF FULL',
                totalWeight: halfFullWeight,
                percentage: config.halfFullPercentage,
            },
            {
                name: 'FULLY LOADED',
                totalWeight: fullyLoadedWeight,
                percentage: config.fullyLoadedPercentage,
            },
            {
                name: '15% OVERLOADED',
                totalWeight: overloadedWeight,
                percentage: config.overloadedPercentage,
            },
        ];
    }

    /**
     * Calculate axle loads based on exact Excel distribution formulas
     * Based on exact Excel formulas from TRAFFIC LOAD.txt
     */
    private static calculateExcelAxleLoads(
        truck: TruckData,
        totalWeight: number
    ): {
        steeringAxleLoad: number;
        singleAxleLoad: number;
        tandemAxleLoad: number;
        tridemAxleLoad: number;
        tandemCount: number; // Number of tandem axles for EALF calculation
    } {
        const axles = this.getTruckAxleCount(truck);
        const steeringAxleLoad = this.calculateSteeringAxleLoad(truck);
        const remainingLoad = totalWeight - steeringAxleLoad;

        let singleAxleLoad = 0;
        let tandemAxleLoad = 0;
        let tridemAxleLoad = 0;
        let tandemCount = 0; // For proper EALF calculation

        // Excel formulas: Different patterns for each truck type based on TRAFFIC LOAD.txt
        if (truck.id === 1) {
            // Camion1 (2 axles): steering + single rear axle
            // G10 = (E10-F10)/($C$11-1) where C11=2
            singleAxleLoad = remainingLoad / (axles - 1);
        } else if (truck.id === 2) {
            // Camion2 (3 axles): steering + tandem rear axles (no single axle)
            // G14 = 0, H14 = (E14-F14)/($C$15-1)*2 where C15=3
            singleAxleLoad = 0;
            tandemAxleLoad = (remainingLoad / (axles - 1)) * 2; // Excel: =(E14-F14)/($C$15-1)*2
            tandemCount = 1; // One tandem pair
        } else if (truck.id === 3) {
            // Camion3 (4 axles): steering + single + tandem configuration
            // G18 = (E18-F18)/($C$19-1) for single
            // H18 = (E18-F18)/($C$19-1)*2 for tandem where C19=4
            const loadPerRearAxle = remainingLoad / (axles - 1);
            singleAxleLoad = loadPerRearAxle; // One single axle
            tandemAxleLoad = loadPerRearAxle * 2; // One tandem pair (2 axles)
            tandemCount = 1;
        } else if (truck.id === 4) {
            // Camion4 (5 axles): steering + single + tridem configuration
            // G22 = (E22-F22)/($C$23-1) for single axle
            // H22 = 0 (no tandem)
            // I22 = (E22-F22)/($C$23-1)*3 for tridem axle where C23=5
            const loadPerRearAxle = remainingLoad / (axles - 1);
            singleAxleLoad = loadPerRearAxle; // One single axle
            tandemAxleLoad = 0; // No tandem axles
            tridemAxleLoad = loadPerRearAxle * 3; // One tridem group (3 axles)
            tandemCount = 0;
        } else if (truck.id === 5) {
            // Camion5 (5 axles): steering + tandem configuration (no single axle)
            // G26 = 0 (no single axle)
            // H26 = (E26-F26)/($C$27-1)*2 for tandem axle where C27=5
            // I26 = 0 (no tridem)
            singleAxleLoad = 0; // No single axles
            tandemAxleLoad = (remainingLoad / (axles - 1)) * 2; // Two tandem pairs (4 axles total)
            tridemAxleLoad = 0; // No tridem axles
            tandemCount = 2; // Two tandem pairs
        } else if (truck.id === 6) {
            // Camion6 (6 axles): steering + tandem + tridem configuration
            // G30 = 0 (no single axle)
            // H30 = (E30-F30)/($C$31-1)*2 for tandem axle
            // I30 = (E30-F30)/($C$31-1)*3 for tridem axle where C31=6
            const loadPerRearAxle = remainingLoad / (axles - 1);
            singleAxleLoad = 0; // No single axles
            tandemAxleLoad = loadPerRearAxle * 2; // One tandem pair (2 axles)
            tridemAxleLoad = loadPerRearAxle * 3; // One tridem group (3 axles)
            tandemCount = 1; // One tandem pair
        } else if (truck.id === 7) {
            // Camion7 (7 axles): steering + tandem configuration
            // G34 = 0 (no single axle)
            // H34 = (E34-F34)/($C$35-1)*2 for tandem axle where C35=7
            // I34 = 0 (no tridem)
            singleAxleLoad = 0; // No single axles
            tandemAxleLoad = (remainingLoad / (axles - 1)) * 2; // Three tandem pairs (6 axles total)
            tridemAxleLoad = 0; // No tridem axles
            tandemCount = 3; // Three tandem pairs
        } else if (truck.id === 8) {
            // Camion8 (8 axles): steering + tandem + tridem configuration
            // G38 = 0 (no single axle)
            // H38 = (E38-F38)/($C$39-1)*2 for tandem axle
            // I38 = (E38-F38)/($C$39-1)*3 for tridem axle where C39=8
            const loadPerRearAxle = remainingLoad / (axles - 1);
            singleAxleLoad = 0; // No single axles
            tandemAxleLoad = loadPerRearAxle * 2; // Two tandem pairs (4 axles)
            tridemAxleLoad = loadPerRearAxle * 3; // One tridem group (3 axles)
            tandemCount = 2; // Two tandem pairs
        } else {
            // Default: distribute evenly across remaining axles as single axles
            singleAxleLoad = remainingLoad / (axles - 1);
        }

        return {
            steeringAxleLoad,
            singleAxleLoad,
            tandemAxleLoad,
            tridemAxleLoad,
            tandemCount,
        };
    }

    /**
     * Convert kN to kips for calculations
     */
    private static knToKips(loadKn: number): number {
        return loadKn * this.EXCEL_CONSTANTS.KN_TO_KIPS;
    }

    /**
     * Update truck ESAL using Excel calculations
     * This method now defaults to dynamic calculations for better accuracy
     * @param truck - The truck data to update
     * @param useDynamic - Whether to use dynamic calculations (default: true)
     * @param structuralNumber - SN value for dynamic calculations (default: 5.0)
     * @param config - Loading scenario configuration (optional, uses default if not provided)
     */
    public static updateTruckESAL(truck: TruckData, useDynamic = true, structuralNumber = 5.0, config?: LoadingScenarioConfig): TruckData {
        const totalVehicles = truck.direction1Vehicles + truck.direction2Vehicles;

        let esalPerDay: number;
        if (useDynamic) {
            // Use dynamic calculation with Excel formulas (recommended)
            esalPerDay = this.calculateTruckESALDynamicNew(truck, totalVehicles, structuralNumber, config);
        } else {
            // Use modern dynamic calculation method
            const dynamicTruck = this.updateTruckESALDynamic(truck, 5.0); // Default SN for compatibility
            esalPerDay = dynamicTruck.esalPerDay || 0;
        }

        return {
            ...truck,
            esalPerDay,
        };
    }

    /**
     * Update truck ESAL using dynamic Excel-based calculations
     * This method replaces hardcoded values with dynamic Excel formula calculations
     * @param config - Loading scenario configuration (optional, uses default if not provided)
     */
    public static updateTruckESALDynamic(truck: TruckData, structuralNumber = 5.0, config?: LoadingScenarioConfig): TruckData {
        const totalVehicles = truck.direction1Vehicles + truck.direction2Vehicles;

        // Use dynamic calculation with Excel formulas
        const esalPerDay = this.calculateTruckESALDynamicNew(truck, totalVehicles, structuralNumber, config);

        return {
            ...truck,
            esalPerDay,
        };
    }

    /**
     * Update truck ESAL using dynamic SN calculation from project data
     * This method uses actual project data to calculate Structural Number dynamically
     * @param config - Loading scenario configuration (optional, uses default if not provided)
     */
    public static updateTruckESALWithDynamicSN(truck: TruckData, project: IProjectData, config?: LoadingScenarioConfig): TruckData {
        const totalVehicles = truck.direction1Vehicles + truck.direction2Vehicles;

        // Calculate dynamic SN from project data
        const structuralNumber = StructuralNumberService.calculateFromProjectData(project);

        // Use dynamic calculation with Excel formulas and actual SN
        const esalPerDay = this.calculateTruckESALDynamicNew(truck, totalVehicles, structuralNumber, config);

        return {
            ...truck,
            esalPerDay,
        };
    }

    /**
     * Calculate ESAL per day for a truck
     * Formula: =$C$10*N10*O10/100 (vehicles/day * EALF * percentage/100)
     */
    public static calculateESALPerDay(vehiclesPerDay: number, ealfPerVehicle: number, passagePercentage: number): number {
        return (vehiclesPerDay * ealfPerVehicle * passagePercentage) / 100;
    }

    /**
     * Calculate total ESAL from all selected trucks
     */
    public static calculateTotalESAL(trucks: TruckData[]): number {
        return trucks.filter(truck => truck.selected).reduce((total, truck) => total + truck.esalPerDay, 0);
    }

    /**
     * Calculate Traffic Growth Factor (TGF)
     * Formula: TGF = [(1 + r)^Y - 1] / ln(1 + r)
     */
    public static calculateTrafficGrowthFactor(growthRate: number, analysisPeriod: number): number {
        const r = growthRate / 100; // Convert percentage to decimal
        const Y = analysisPeriod;

        if (r === 0) {
            return Y; // If no growth, TGF equals the analysis period
        }

        return (Math.pow(1 + r, Y) - 1) / Math.log(1 + r);
    }

    /**
     * Calculate FAA projected ESAL using simplified formula
     * Formula: w'18 = (1 + (r * Y / 200)) * N1 * Y
     * Where:
     * - N1 = ESAL per day * 365 (first year ESAL)
     * - r = growth rate as percentage
     * - Y = analysis period in years
     */
    public static calculateFAAProjectedESAL(esalPerDay: number, analysisPeriod: number, growthRate: number): number {
        const N1 = esalPerDay * 365; // Convert daily ESAL to yearly
        const r = growthRate; // Growth rate as percentage (not decimal)
        const Y = analysisPeriod;

        // FAA simplified formula: w'18 = (1 + (r * Y / 200)) * N1 * Y
        return (1 + (r * Y) / 200) * N1 * Y;
    }

    /**
     * Calculate comprehensive ESAL summary data
     */
    public static calculateSummaryData(
        trucks: TruckData[],
        directionalDistribution: number = ESAL_CONSTANTS.DEFAULT_DD,
        laneDistribution: number = ESAL_CONSTANTS.DEFAULT_DL,
        analysisPeriod: number = ESAL_CONSTANTS.DEFAULT_ANALYSIS_PERIOD,
        yearGrowthRate: number = ESAL_CONSTANTS.DEFAULT_GROWTH_RATE
    ): SummaryCalculationData {
        // Calculate total passages N1 (sum of all vehicles per day from both directions)
        const totalPassages = trucks.reduce((sum, truck) => {
            if (truck.selected) {
                return sum + truck.direction1Vehicles + truck.direction2Vehicles;
            }
            return sum;
        }, 0);

        // Calculate Traffic Growth Factor
        const compoundTrafficGrowthFactor = this.calculateTrafficGrowthFactor(yearGrowthRate, analysisPeriod);

        // Calculate total volume of traffic during analysis period
        // w'18 = TGF * N1
        const totalVolumeTraffic = compoundTrafficGrowthFactor * totalPassages;

        // Calculate traffic in design lane
        // w18 = DD * DL * w'18
        const trafficDesignLane = directionalDistribution * laneDistribution * totalVolumeTraffic;

        return {
            totalPassages,
            directionalDistribution,
            laneDistribution,
            analysisPeriod,
            yearGrowthRate,
            compoundTrafficGrowthFactor,
            totalVolumeTraffic,
            trafficDesignLane,
            userDefinedTrafficDesignLane: 0, // Default to 0 (use calculated value)
        };
    }

    /**
     * Validate input values
     */
    public static validateInput(value: number, min = 0, max = Infinity): boolean {
        return !isNaN(value) && isFinite(value) && value >= min && value <= max;
    }

    /**
     * Format number for display (limit decimal places)
     */
    public static formatNumber(value: number, decimals = 2): string {
        return isFinite(value) ? value.toFixed(decimals) : '0.00';
    }

    /**
     * Debug utility: Compare dynamic vs hardcoded calculations
     * This method helps verify that dynamic calculations are working correctly
     * @param config - Loading scenario configuration (optional, uses default if not provided)
     */
    public static compareCalculationMethods(
        truck: TruckData,
        structuralNumber = 5.0,
        config?: LoadingScenarioConfig
    ): {
        dynamic: number;
        modern: number;
        difference: number;
        percentageDifference: number;
    } {
        const totalVehicles = truck.direction1Vehicles + truck.direction2Vehicles;

        const dynamicResult = this.calculateTruckESALDynamicNew(truck, totalVehicles, structuralNumber, config);
        const modernResult = this.updateTruckESALDynamic(truck, structuralNumber, config).esalPerDay || 0;

        const difference = dynamicResult - modernResult;
        const percentageDifference = modernResult !== 0 ? (difference / modernResult) * 100 : 0;

        return {
            dynamic: dynamicResult,
            modern: modernResult,
            difference,
            percentageDifference,
        };
    }

    /**
     * Calculate aircraft ESAL using the values from the axle load columns
     * This method uses the actual values from the Excel table instead of calculating them dynamically
     * @param aircraft - Aircraft data with pre-defined axle loads
     * @param structuralNumber - Structural number for calculations (default: 5.0)
     * @returns Updated aircraft data with calculated ESAL
     */
    public static updateAircraftESAL(aircraft: AircraftData, structuralNumber = 5.0): AircraftData {
        // Return early if aircraft is not selected or has no movements
        const totalMovements = (aircraft.runway1Vehicles || 0) + (aircraft.runway2Vehicles || 0);
        console.log(`Aircraft ${aircraft.id} (${aircraft.name}): selected=${aircraft.selected}, movements=${totalMovements}`);

        if (!aircraft.selected || totalMovements <= 0) {
            // Calculate EALF even for aircraft with 0 movements to verify against Excel
            if (aircraft.selected) {
                // Get aircraft configuration to determine axle counts
                const config = DEFAULT_AIRCRAFT_CONFIGURATIONS.find(c => c.id === aircraft.id);
                if (config) {
                    const steeringLoadPerAxleKips = (aircraft.steeringAxleLoad / Math.max(1, config.steeringAxles)) * this.EXCEL_CONSTANTS.KN_TO_KIPS;
                    const tandemLoadKips = aircraft.tandemAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;
                    const tridemLoadKips = aircraft.tridemAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;

                    let totalEALF = 0;

                    if (steeringLoadPerAxleKips > 0) {
                        const steeringEALF = this.calculateEALF(steeringLoadPerAxleKips, this.EXCEL_CONSTANTS.T8, structuralNumber);
                        totalEALF += steeringEALF;
                    }

                    if (aircraft.singleAxleLoad > 0) {
                        const singleLoadKips = aircraft.singleAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;
                        const singleEALF = this.calculateEALF(singleLoadKips, this.EXCEL_CONSTANTS.V8, structuralNumber);
                        totalEALF += singleEALF;
                    }

                    if (tandemLoadKips > 0) {
                        const tandemEALF = this.calculateEALF(tandemLoadKips, this.EXCEL_CONSTANTS.X8, structuralNumber);
                        totalEALF += tandemEALF;
                    }

                    if (tridemLoadKips > 0) {
                        const tridemEALF = this.calculateEALF(tridemLoadKips, this.EXCEL_CONSTANTS.Z8, structuralNumber);
                        totalEALF += tridemEALF;
                    }

                    console.log(`Aircraft ${aircraft.id}: EALF = ${totalEALF.toFixed(2)} (0 movements, ESAL = 0)`);
                }
            }

            console.log(`Aircraft ${aircraft.id}: Returning ESAL = 0 (not selected or no movements)`);
            return {
                ...aircraft,
                esalPerDay: 0,
                passagePercentage: totalMovements > 0 ? 100 : 0,
            };
        }

        // Get aircraft configuration to determine axle counts
        const config = DEFAULT_AIRCRAFT_CONFIGURATIONS.find(c => c.id === aircraft.id);
        if (!config) {
            console.error(`No configuration found for aircraft ${aircraft.id}`);
            return { ...aircraft, esalPerDay: 0, passagePercentage: 0 };
        }

        // Convert axle loads from kN to kips for EALF calculations
        // IMPORTANT: EALF calculations use load PER AXLE, not total load for axle group
        const steeringLoadPerAxleKips = (aircraft.steeringAxleLoad / Math.max(1, config.steeringAxles)) * this.EXCEL_CONSTANTS.KN_TO_KIPS;
        const tandemLoadKips = aircraft.tandemAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;
        const tridemLoadKips = aircraft.tridemAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;

        console.log(
            `Aircraft ${aircraft.id} axle loads: steering=${aircraft.steeringAxleLoad}kN/${config.steeringAxles}axles (${steeringLoadPerAxleKips}kips per axle), tandem=${aircraft.tandemAxleLoad}kN (${tandemLoadKips}kips), tridem=${aircraft.tridemAxleLoad}kN (${tridemLoadKips}kips)`
        );

        // Calculate EALF for each axle type using Excel formulas
        let totalEALF = 0;

        // Steering axle EALF: Formula from Excel J13 = 1/(((19/(T13+T8))^4.79)*((10^(GT/U13))/(10^(GT/Beta18))*(T8^4.33)))
        if (steeringLoadPerAxleKips > 0) {
            const steeringEALF = this.calculateEALF(steeringLoadPerAxleKips, this.EXCEL_CONSTANTS.T8, structuralNumber);
            console.log(`Aircraft ${aircraft.id}: steering EALF = ${steeringEALF}`);
            totalEALF += steeringEALF;
        }

        // Single axle EALF: Use complete Excel formula K13 = 1/(((19/(V13+V8))^4.79)*((10^(GT/W13))/(10^(GT/Beta18))*(V8^4.33)))
        if (aircraft.singleAxleLoad > 0) {
            const singleLoadKips = aircraft.singleAxleLoad * this.EXCEL_CONSTANTS.KN_TO_KIPS;
            const singleEALF = this.calculateEALF(singleLoadKips, this.EXCEL_CONSTANTS.V8, structuralNumber);
            console.log(`Aircraft ${aircraft.id}: single EALF = ${singleEALF}`);
            totalEALF += singleEALF;
        }

        // Tandem axle EALF: Formula from Excel L13 = 1/(((19/(X13+X8))^4.79)*((10^(GT/Y13))/(10^(GT/Beta18))*(X8^4.33)))
        if (tandemLoadKips > 0) {
            const tandemEALF = this.calculateEALF(tandemLoadKips, this.EXCEL_CONSTANTS.X8, structuralNumber);
            console.log(`Aircraft ${aircraft.id}: tandem EALF = ${tandemEALF}`);
            totalEALF += tandemEALF;
        }

        // Tridem axle EALF: Formula from Excel M13 = 1/(((19/(Z13+Z8))^4.79)*((10^(GT/AA13))/(10^(GT/Beta18))*(Z8^4.33)))
        if (tridemLoadKips > 0) {
            const tridemEALF = this.calculateEALF(tridemLoadKips, this.EXCEL_CONSTANTS.Z8, structuralNumber);
            console.log(`Aircraft ${aircraft.id}: tridem EALF = ${tridemEALF}`);
            totalEALF += tridemEALF;
        }

        // Calculate ESAL per day: P = movements/day * EALF * 100% (since we use 100% loading scenario)
        const esalPerDay = totalMovements * totalEALF;
        console.log(`Aircraft ${aircraft.id}: movements=${totalMovements}, totalEALF=${totalEALF}, esalPerDay=${esalPerDay}`);

        return {
            ...aircraft,
            esalPerDay,
            passagePercentage: 100, // Aircraft always use 100% loading scenario
        };
    }

    /**
     * Calculate dynamic axle loads based on total weight and load distribution percentages
     * This mirrors Excel's calculation where each axle type gets a percentage of the total weight
     * @param totalWeight - Total aircraft weight in kN
     * @param config - Aircraft configuration with axle counts and load percentages
     * @returns Object with calculated axle loads for each type
     */
    public static calculateDynamicAxleLoads(
        totalWeight: number,
        config: Pick<
            AircraftConfiguration,
            'steeringAxles' | 'singleAxles' | 'tandemAxles' | 'tridemAxles' | 'steeringLoadPercentage' | 'singleLoadPercentage' | 'tandemLoadPercentage' | 'tridemLoadPercentage'
        >
    ): {
        steeringAxleLoad: number;
        singleAxleLoad: number;
        tandemAxleLoad: number;
        tridemAxleLoad: number;
    } {
        // Calculate total load for each axle type based on percentage
        const steeringTotalLoad = totalWeight * (config.steeringLoadPercentage / 100);
        const singleTotalLoad = totalWeight * (config.singleLoadPercentage / 100);
        const tandemTotalLoad = totalWeight * (config.tandemLoadPercentage / 100);
        const tridemTotalLoad = totalWeight * (config.tridemLoadPercentage / 100);

        // Calculate load per axle (divide by number of axles of each type)
        // Excel formula: Axle Load = (Total Weight * % Load) / Number of Axles
        const steeringAxleLoad = config.steeringAxles > 0 ? steeringTotalLoad / config.steeringAxles : 0;
        const singleAxleLoad = config.singleAxles > 0 ? singleTotalLoad / config.singleAxles : 0;
        const tandemAxleLoad = config.tandemAxles > 0 ? tandemTotalLoad / config.tandemAxles : 0;
        const tridemAxleLoad = config.tridemAxles > 0 ? tridemTotalLoad / config.tridemAxles : 0;

        return {
            steeringAxleLoad,
            singleAxleLoad,
            tandemAxleLoad,
            tridemAxleLoad,
        };
    }
}
